import { scrubPIIFromDataLayer } from '@/analytics/scrubPIIFromAnalytics';
import type { DataLayer, PayloadItems } from '@/analytics/types';
import { EventName } from '@/analytics/types';
import useCurrency from '@/app/hooks/useCurrency';
import logToDatadog from '@/logger/logToDatadog';
import { getCartIdCookie } from '@/utils/cartUtils';
import { getCookie } from '@/utils/cookie';

const ORDER_COMPLETE_TRACK_FIRED = 'order complete';
const BRAND = 'Oura Ring';

export interface UseProductAnalytics {
  sendProductAddedEvent: (payload: PayloadItems) => Promise<void>;
  sendProductViewedEvent: (payload: PayloadItems) => Promise<void>;
}

export const useProductAnalytics = (): UseProductAnalytics => {
  const { currencyCode } = useCurrency();

  // Product Viewed
  const sendProductViewedEvent = async (payload: PayloadItems) => {
    const product = payload.product;
    const id = product?.id ?? 0;
    const imageUrl = product?.lineItemImage?.originalSrc;
    const price = product?.price?.amount ?? 0;
    const title = product?.title ?? '';
    const variants = product?.variants;
    const variant = variants?.[0];
    const variantId = variant?.id ?? '';
    const sku = variant?.sku;
    const category = product?.analytics_category ?? product?.product_type ?? '';
    const url = `https://www.ouraring.com/${payload.path}`;
    const fbc = getCookie('_fbc');
    const fbp = getCookie('_fbp');

    // GA4
    const ga4Data: DataLayer = {
      event: EventName.ViewItem,
      ecommerce_data: {
        items: [
          {
            item_name: title,
            item_id: id,
            price,
            item_brand: BRAND,
            item_category: category,
            item_variant: variantId,
            quantity: 1,
          },
        ],
      },
    };
    try {
      window.dataLayer?.push(ga4Data);
    } catch (error) {
      await logToDatadog('analytics', `dataLayer push failed in ga4 Service`, {
        ...scrubPIIFromDataLayer(ga4Data),
        ...(error as Record<string, unknown>),
      });
    }

    // segment
    await window.ouraAnalytics.track(EventName.ProductViewed, {
      product_id: id,
      sku,
      category,
      name: title,
      brand: BRAND,
      variant: variantId,
      price,
      quantity: 1,
      coupon: '',
      position: 3,
      url,
      image_url: imageUrl,
      currency: currencyCode,
      fbc,
      fbp,
    });
  };

  // Product added to cart
  const sendProductAddedEvent = async (payload: PayloadItems) => {
    const product = payload.product;
    const cartId = getCartIdCookie();
    const id = product?.id ?? 0;
    const imageUrl = product?.lineItemImage?.originalSrc;
    const price = product?.price?.amount ?? 0;
    const title = product?.title ?? '';
    const variant = product?.variants[0];
    const variantId = variant?.id ?? '';
    const sku = variant?.sku;
    const category = product?.analytics_category ?? product?.product_type ?? '';
    const url = `https://www.ouraring.com/${payload.path}`;
    const fbc = getCookie('_fbc');
    const fbp = getCookie('_fbp');
    const variant_name = payload.variant_name;
    const stockStatus = payload.stockStatus;

    // remove localStorage record of any previous order completed event
    // DO NOT REMOVE THIS LINE
    localStorage.removeItem(ORDER_COMPLETE_TRACK_FIRED);

    // GA4
    const ga4Data: DataLayer = {
      event: EventName.AddToCart,
      ecommerce_data: {
        items: [
          {
            item_name: title,
            item_id: id,
            price,
            item_brand: BRAND,
            item_category: category,
            item_variant: variantId,
            quantity: 1,
          },
        ],
      },
    };
    try {
      window.dataLayer?.push(ga4Data);
    } catch (error) {
      await logToDatadog('analytics', `dataLayer push failed in ga4 Service`, {
        ...scrubPIIFromDataLayer(ga4Data),
        ...(error as Record<string, unknown>),
      });
    }

    // segment
    await window.ouraAnalytics.track(EventName.ProductAdded, {
      cart_id: cartId,
      product_id: id,
      sku,
      category,
      name: title,
      brand: BRAND,
      variant: variantId,
      price,
      quantity: 1,
      coupon: '',
      position: 3,
      url,
      image_url: imageUrl,
      currency: currencyCode,
      fbc,
      fbp,
      variant_name,
      stockStatus,
    });
  };

  return { sendProductAddedEvent, sendProductViewedEvent };
};

export default useProductAnalytics;
